import { useCallback, useEffect, useMemo, useState } from 'react'
import MainLayout from '../layout/MainLayout/MainLayout'
import { IconAddWindow, IconFolderPlus } from '../layout/NavRail/NavRail'
import NavLocation from '../layout/NavLocation/NavLocation'
import NavList from '../layout/NavList/NavList'
import CodeBreadcrumb from '../components/code/CodeBreadcrumb'
import CodeViewToggle from '../components/code/CodeViewToggle'
import TopicsNav from '../components/topics/TopicsNav'
import TopicWindowBody from '../components/topics/TopicWindowBody'
import useTopicsWorkspace from '../components/topics/useTopicsWorkspace'
import { WindowGrid, GridDock, useWindowGrid } from '../components/grid'
import { useTopics } from '../hooks/useTopics'
import { useModal } from '../context/ModalContext'
import { usePageTitle } from '../components/DocumentTitle'
import { topicLabelFor } from '../utils/topicLabel'
import '../components/inputs/inputs.css'

const WINDOW_LIMITS = { maxWindows: 6, maxPerColumn: 2, maxColumns: 3 }

// Topics workspace. The sidebar browses the topic tree the way Code browses a
// standard: drill one level at a time, or show the indented tree. Each window
// remembers its own place and lists the bookmarks filed under its topic.
function TopicsPage() {
  const { openModal } = useModal()
  const { topics, status, reload, renameTopic, removeTopic, duplicateTopic } = useTopics()
  const [notice, setNotice] = useState('')

  const grid = useWindowGrid({
    storageKey: 'ct-grid-topics-v1',
    limits: WINDOW_LIMITS,
    initialType: 'topic',
  })
  const workspace = useTopicsWorkspace(grid, topics)

  useEffect(() => {
    if (!notice) return undefined
    const timer = setTimeout(() => setNotice(''), 4000)
    return () => clearTimeout(timer)
  }, [notice])

  const byId = useMemo(() => {
    const map = new Map()
    topics.forEach((topic) => map.set(String(topic._id), topic))
    return map
  }, [topics])
  const labelOf = useMemo(() => topicLabelFor(topics), [topics])
  const searching = workspace.query.trim().length > 0

  const handleAddWindow = useCallback(() => {
    if (grid.isFull) {
      setNotice(
        `You can open up to ${grid.limits.maxWindows} windows at once. Close one to add another.`
      )
      return
    }
    grid.addWindow({ type: 'topic' })
  }, [grid])

  const handleOpen = useCallback(
    (row) => {
      workspace.select(row)
      grid.selectEntity({ type: 'topic', entityId: row._id })
    },
    [workspace, grid]
  )

  const handleDuplicate = useCallback(
    (topic) => {
      duplicateTopic(topic._id).catch((err) => {
        setNotice(err.message || 'Could not duplicate the topic.')
      })
    },
    [duplicateTopic]
  )

  const handleRename = useCallback(
    (topic) => {
      openModal('chat-text', {
        title: 'Rename topic',
        label: 'Name',
        value: topic.name,
        placeholder: 'Topic name',
        onSave: async (text) => {
          const name = String(text || '').trim()
          if (!name) throw new Error('A topic name is required.')
          await renameTopic(topic._id, name)
        },
      })
    },
    [openModal, renameTopic]
  )

  const handleDelete = useCallback(
    (topic) => {
      const nested = workspace.hasChildren(topic)
      openModal('confirm', {
        title: 'Delete topic',
        message: nested
          ? `Delete "${topic.name}"? Bookmarks filed under it are kept and become unfiled. Topics inside it move up one level.`
          : `Delete "${topic.name}"? Bookmarks filed under it are kept and become unfiled.`,
        confirmLabel: 'Delete',
        danger: true,
        onConfirm: () => removeTopic(topic._id),
        onConfirmed: () => {
          workspace.forgetTopic(topic._id)
          grid.columns.forEach((col) =>
            col.windows.forEach((win) => {
              if (String(win.entityId) === String(topic._id)) grid.setWindowEntity(win.id, null)
            })
          )
          reload()
        },
      })
    },
    [workspace, openModal, removeTopic, grid, reload]
  )

  const handleAddChild = useCallback(
    (topic) => {
      openModal('create-topic', {
        parentId: topic._id,
        parentName: topic.name,
        onSaved: (created) => {
          if (!created?._id) return
          reload()
          workspace.select(created)
          grid.selectEntity({ type: 'topic', entityId: created._id })
        },
      })
    },
    [openModal, reload, workspace, grid]
  )

  const handleNewTopic = useCallback(() => {
    const parent = workspace.current
    openModal('create-topic', {
      parentId: parent?._id,
      parentName: parent?.name,
      onSaved: (topic) => {
        if (!topic?._id) return
        reload()
        workspace.select(topic)
        grid.selectEntity({ type: 'topic', entityId: topic._id })
      },
    })
  }, [workspace, openModal, reload, grid])

  const { navById } = workspace
  const getTitle = useCallback(
    (win) => {
      const id = navById[win.id]?.topicId
      if (!id) return 'New window'
      const topic = byId.get(String(id))
      if (!topic) return status === 'loading' ? 'Loading…' : 'Topic'
      return labelOf(topic)
    },
    [navById, byId, labelOf, status]
  )

  const activeId = navById[grid.activeId]?.topicId
  usePageTitle(activeId && byId.get(String(activeId)) ? getTitle({ id: grid.activeId }) : '')

  const hopName = useCallback(
    (entry) => {
      if (!entry) return 'Topic'
      return byId.get(String(entry.id))?.name || entry.name || 'Topic'
    },
    [byId]
  )

  const handleReveal = useCallback(
    (windowId) => {
      grid.focusWindow(windowId)
      workspace.revealTopic(windowId)
    },
    [grid, workspace]
  )

  const renderWindow = useCallback(
    (win) => {
      const nav = navById[win.id]
      const index = Number.isInteger(nav?.historyIndex) ? nav.historyIndex : -1
      const history = nav?.history || []
      return (
        <TopicWindowBody
          topicId={nav?.topicId || null}
          topics={topics}
          history={history}
          historyIndex={index}
          backTitle={history[index - 1] ? `Back to ${hopName(history[index - 1])}` : 'Back'}
          forwardTitle={history[index + 1] ? `Forward to ${hopName(history[index + 1])}` : 'Forward'}
          onHistory={(dir) => workspace.stepHistory(win.id, dir)}
          onLocate={() => handleReveal(win.id)}
        />
      )
    },
    [navById, topics, hopName, workspace, handleReveal]
  )

  const navActions = useMemo(
    () => [
      {
        key: 'add-window',
        label: 'Add window',
        icon: <IconAddWindow />,
        onClick: handleAddWindow,
      },
      {
        key: 'new-topic',
        label: 'New topic',
        icon: <IconFolderPlus />,
        onClick: handleNewTopic,
      },
    ],
    [handleAddWindow, handleNewTopic]
  )

  const crumbTrail = workspace.trail.map((topic) => ({ label: topic.name }))

  return (
    <MainLayout
      storageId="topics-layout"
      navLocation={
        <NavLocation>
          <div className="code-locationbar">
            <CodeBreadcrumb rootLabel="Topics" trail={crumbTrail} onNavigate={workspace.goTo} />
            <CodeViewToggle mode={workspace.mode} onChange={workspace.setMode} />
          </div>
        </NavLocation>
      }
      navActions={navActions}
      navDock={
        <GridDock
          items={grid.minimized}
          getTitle={getTitle}
          onRestore={grid.restoreWindow}
          onClose={grid.closeWindow}
        />
      }
      nav={
        <NavList>
          <div className="topic-nav-search">
            <input
              className="ct-input"
              type="text"
              value={workspace.query}
              placeholder="Search topics…"
              onChange={(event) => workspace.setQuery(event.target.value)}
            />
          </div>
          <TopicsNav
            status={status}
            rows={workspace.rows}
            atRoot={workspace.atRoot}
            mode={workspace.mode}
            selectedId={workspace.selectedId}
            hasChildren={workspace.hasChildren}
            depthOf={workspace.depthOf}
            isExpanded={workspace.isExpanded}
            isDimmed={workspace.isDimmed}
            onOpen={handleOpen}
            onForward={workspace.drillInto}
            onToggleCollapse={workspace.toggleCollapse}
            titleOf={labelOf}
            searching={searching}
            onAddChild={handleAddChild}
            onDuplicate={handleDuplicate}
            onRename={handleRename}
            onDelete={handleDelete}
            scrollToId={workspace.revealSignal.id}
            scrollNonce={workspace.revealSignal.nonce}
          />
        </NavList>
      }
      display={
        <div className="sr-grid-wrap">
          {notice && <div className="sr-grid-notice">{notice}</div>}
          <div className="sr-grid-body">
            <WindowGrid
              grid={grid}
              renderWindow={renderWindow}
              getTitle={getTitle}
              empty="Select a topic to get started."
            />
          </div>
        </div>
      }
    />
  )
}

export default TopicsPage
